import ChecklistCollection from '@repo/datalayer/store/models/ChecklistCollection'
import Checklist from '@repo/datalayer/store/models/Checklist'
import { observer } from 'mobx-react-lite'
import { useRouter } from '../../hooks/router'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '@/auth'
import { Trash2, ChevronRight, ListChecks } from 'lucide-react'

function CircularProgress({ percent }: { percent: number }) {
  const circumference = 2 * Math.PI * 20
  const strokeDashoffset = circumference - (percent / 100) * circumference

  return (
    <div className="relative w-[4.5rem] h-[4.5rem] flex-shrink-0">
      <svg className="w-[4.5rem] h-[4.5rem] -rotate-90" viewBox="0 0 48 48">
        <circle
          cx="24"
          cy="24"
          r="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-border"
        />
        <circle
          cx="24"
          cy="24"
          r="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          className="text-primary transition-all duration-500"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: strokeDashoffset,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{Math.round(percent)}%</span>
      </div>
    </div>
  )
}

export const CurrentChecklists = observer(function CurrentChecklists({
  checklists,
  onDelete,
}: {
  checklists: ChecklistCollection
  onDelete: (id: string) => void
}) {
  const router = useRouter()
  const user = useCurrentUser()
  const { t } = useTranslation()

  if (checklists.items.length === 0) {
    return null
  }

  return (
    <div data-test-id="current-checklists-section" className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-xl bg-primary/10">
          <ListChecks className="w-5 h-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold">{t('home.activeChecklists')}</h2>
      </div>
      <div
        className={`grid gap-4 ${checklists.items.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {checklists.items.map((checklist) => {
          const currentUserIsCreator = user.id === checklist.creatorId
          const total = checklist.items.length
          const completed = checklist.items.filter(
            (i) => i.complete || i.skipped
          ).length
          const percent = total > 0 ? (completed / total) * 100 : 0

          return (
            <div
              className="group relative bg-card border-2 border-transparent hover:border-primary/20 rounded-2xl overflow-hidden cursor-pointer shadow-card transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              data-test-id="checklist-card"
              data-checklist-name={checklist.name}
              key={checklist.id}
              onClick={() => {
                router.navigateToChecklist(checklist.id)
              }}
            >
              {/* Gradient accent bar */}
              <div className="h-1.5 bg-primary" />

              <div className="p-5">
                <div className="flex items-center gap-5">
                  <CircularProgress percent={percent} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg leading-tight truncate mb-1">
                      {checklist.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {completed} of {total} items completed
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Progress bar at bottom */}
                <div className="mt-4 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>

              {currentUserIsCreator && (
                <ConfirmDialog
                  destructive
                  confirmLabel="Delete"
                  title="Are you sure?"
                  description="This will delete the checklist and all its items."
                  onConfirm={async () => {
                    onDelete(checklist.id)
                  }}
                >
                  <button
                    className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all duration-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trash2 size={16} />
                  </button>
                </ConfirmDialog>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
})
